import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { supabase } from '../../../api/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import WarehouseLayout from '../WarehouseLayout'
import { inputCls, btnSecondary, fmtDate } from '../types'
import type { DeliveryNote } from '../types'
import { useStickyHScroll } from '../../../hooks/useStickyHScroll'
import { StickyHScrollbar } from '../../../components/StickyHScrollbar'
import { usePagination } from '../../../hooks/usePagination'
import PaginationControls from '../../../components/PaginationControls'
import { classifyOverdue, daysBetween, OVERDUE_ROW_CLASS } from '../../../lib/overdue'
import { NoteSourceLink, useNoteSources } from '../NoteSourceLink'

// Z50.X (Option 1 Replace): this page used to read warehouse_inventory rows with
// status='planned' + dispatch_mode/dispatch_carrier/etc. -- those columns never actually
// existed on the live table (confirmed via PostgREST), and warehouse_inventory had zero
// rows in any status but 'in_inbound' at last check, so there was nothing there to carry
// forward. Now reads delivery_notes directly: "planned" = lifecycle_status='PLANNED' AND
// status='ACTIVE' (a superseded/voided note has no business showing up here even if it
// still has a stale lifecycle_status).

const TYPE_LABEL: Record<DeliveryNote['note_type'], string> = {
  DELIVERY: 'Delivery', COLLECTION_COURIER: 'Collection (Courier)',
  COLLECTION_SELF: 'Collection (Self)', COLLECTION_SUPPLIER: 'Collection (Supplier)',
}
const TYPE_CLS: Record<DeliveryNote['note_type'], string> = {
  DELIVERY:            'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  COLLECTION_COURIER:  'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  COLLECTION_SELF:     'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  COLLECTION_SUPPLIER: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
}

type DateFilter = 'today' | 'week' | 'month' | 'next7' | 'custom'
type ReadyFilter = 'all' | 'ready' | 'not_ready'

const READINESS_BADGE: Record<NonNullable<DeliveryNote['readiness_status']>, { icon: string; label: string; cls: string }> = {
  PENDING_ARRIVAL:  { icon: '🔴', label: 'Pending Arrival',  cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  PENDING_INBOUND:  { icon: '🟠', label: 'Pending Inbound',  cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  READY:            { icon: '🟢', label: 'Ready',            cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
}
// Older notes and any row a query somehow returns without the column set -- treat as Ready
// rather than inventing a "not ready" alarm with no evidence either way.
function readinessOf(n: DeliveryNote): NonNullable<DeliveryNote['readiness_status']> {
  return n.readiness_status ?? 'READY'
}
const READINESS_SORT_RANK: Record<NonNullable<DeliveryNote['readiness_status']>, number> = {
  READY: 0, PENDING_INBOUND: 1, PENDING_ARRIVAL: 2,
}

function ukToday() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' }) // YYYY-MM-DD
}
function addDays(dateStr: string, n: number) {
  const d = new Date(dateStr + 'T00:00:00')
  d.setDate(d.getDate() + n)
  return d.toISOString().slice(0, 10)
}
// Monday-start ISO date of the current UK week
function ukWeekStart() {
  const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/London' }))
  const day = now.getDay() // 0=Sun..6=Sat
  const diff = day === 0 ? 6 : day - 1
  now.setDate(now.getDate() - diff)
  return now.toISOString().slice(0, 10)
}
function ukMonthStart() {
  const today = ukToday()
  return today.slice(0, 7) + '-01'
}

export default function PlannedOutboundPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const [notes, setNotes] = useState<DeliveryNote[]>([])
  const noteSources = useNoteSources(notes)
  const [splitNosByNote, setSplitNosByNote] = useState<Record<string, string[]>>({})
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState<string | null>(null)

  // The nav badge counts not-ready notes with no date restriction -- widen the default
  // range when deep-linked so the count staff just saw isn't silently filtered back out.
  const [dateFilter, setDateFilter] = useState<DateFilter>(searchParams.get('ready') === 'not_ready' ? 'month' : 'week')
  const [customFrom, setCustomFrom] = useState(ukToday())
  const [customTo, setCustomTo] = useState(addDays(ukToday(), 7))
  // Deep-linked from the "Planned Outbound ⚠️N" nav tab badge -- lands pre-filtered so staff
  // go straight to the not-ready queue instead of having to find the filter themselves.
  const [readyFilter, setReadyFilter] = useState<ReadyFilter>(searchParams.get('ready') === 'not_ready' ? 'not_ready' : 'all')

  const [completeNote, setCompleteNote] = useState<DeliveryNote | null>(null)
  const [completeForm, setCompleteForm] = useState({ at: '', vehicle: '', driver: '', notes: '', signed: false })
  const [completeForceReason, setCompleteForceReason] = useState('')
  const [rescheduleNote, setRescheduleNote] = useState<DeliveryNote | null>(null)
  const [rescheduleDate, setRescheduleDate] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const fetchNotes = useCallback(async () => {
    setLoading(true)
    // This page is the Collection board (Gary, 2026-09-24, merged single-note model
    // 2026-09-28): every note (Collection Only or Collection+Delivery) lands here first,
    // regardless of fulfilment_type -- confirming it collected (submitComplete below) either
    // sends it straight to Outbound (Collection Only) or flips it to IN_TRANSIT and off this
    // board onto the Deliveries board instead (Collection+Delivery), on the same row either way.
    const { data } = await supabase.from('delivery_notes')
      .select('*, devannings!inner(warehouse_id)')
      .eq('devannings.warehouse_id', id!)
      .eq('lifecycle_status', 'PLANNED').eq('status', 'ACTIVE')
      // Collection Only notes never get a transit_status at all (null); a Collection+Delivery
      // note starts PENDING (not yet collected, still belongs here) and only leaves once
      // IN_TRANSIT (moved to the Deliveries board instead).
      .or('transit_status.is.null,transit_status.eq.PENDING')
      .order('scheduled_date', { ascending: true })
    const rows = (data as DeliveryNote[] | null) || []
    setNotes(rows)

    const allItemIds = [...new Set(rows.flatMap(n => n.item_ids || []))]
    if (allItemIds.length) {
      const { data: its } = await supabase.from('devanning_items').select('id, split_no').in('id', allItemIds)
      const splitByItem = Object.fromEntries(((its as { id: string; split_no: string | null }[] | null) || []).map(i => [i.id, i.split_no]))
      const map: Record<string, string[]> = {}
      for (const n of rows) {
        map[n.id] = [...new Set((n.item_ids || []).map(iid => splitByItem[iid]).filter(Boolean))] as string[]
      }
      setSplitNosByNote(map)
    } else {
      setSplitNosByNote({})
    }
    setLoading(false)
  }, [id])

  useEffect(() => { fetchNotes() }, [fetchNotes])

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 3500)
  }

  const [rangeFrom, rangeTo] = useMemo(() => {
    const today = ukToday()
    switch (dateFilter) {
      case 'today': return [today, today]
      case 'week':  return [ukWeekStart(), addDays(ukWeekStart(), 6)]
      case 'month': return [ukMonthStart(), addDays(ukMonthStart(), 31)]
      case 'next7': return [today, addDays(today, 7)]
      case 'custom': return [customFrom, customTo]
    }
  }, [dateFilter, customFrom, customTo])

  const inDateRange = notes.filter(n => n.scheduled_date >= rangeFrom && n.scheduled_date <= rangeTo)
  const notReadyCount = inDateRange.filter(n => readinessOf(n) !== 'READY').length
  const visible = inDateRange
    .filter(n => readyFilter === 'all' ? true : readyFilter === 'ready' ? readinessOf(n) === 'READY' : readinessOf(n) !== 'READY')
    // Ready-first (staff should action the ones that can actually go out first), then
    // by scheduled date/time within each readiness group.
    .slice()
    .sort((a, b) => READINESS_SORT_RANK[readinessOf(a)] - READINESS_SORT_RANK[readinessOf(b)] || a.scheduled_date.localeCompare(b.scheduled_date))

  // Z35-style pagination -- stat cards below read `visible` (filtered, unpaginated) so
  // they're never affected by which page is showing; only the table body is sliced.
  const { page, setPage, pageSize, setPageSize } = usePagination('planned-outbound-list')
  useEffect(() => { setPage(0) }, [dateFilter, customFrom, customTo, readyFilter]) // eslint-disable-line react-hooks/exhaustive-deps
  const pagedTotal = visible.length
  const paged = pageSize === 'all' ? visible : visible.slice(page * pageSize, page * pageSize + pageSize)
  const plannedCollectionOnly = visible.filter(n => n.fulfilment_type !== 'COLLECTION_AND_DELIVERY').length
  const plannedCollectAndDeliver = visible.filter(n => n.fulfilment_type === 'COLLECTION_AND_DELIVERY').length
  const totalPlts = visible.reduce((s, n) => s + n.pallets_qty, 0)
  const totalCtns = visible.reduce((s, n) => s + n.nop, 0)

  function openComplete(note: DeliveryNote) {
    setCompleteNote(note)
    setCompleteForm({ at: new Date().toISOString().slice(0, 16), vehicle: note.vehicle_reg || '', driver: note.driver_name || '', notes: '', signed: false })
    setCompleteForceReason('')
  }
  async function submitComplete() {
    if (!completeNote) return
    const notReady = readinessOf(completeNote) !== 'READY'
    if (notReady) {
      const label = READINESS_BADGE[readinessOf(completeNote)].label
      if (!window.confirm(`⚠️ This note's goods haven't been inbounded yet (status: ${label}). Mark as shipped anyway? This may cause inventory data to become inconsistent.`)) return
      if (!completeForceReason.trim()) { alert('Reason required to force-complete a not-ready note.'); return }
    }
    setSubmitting(true)
    try {
      const isCollectAndDeliver = completeNote.fulfilment_type === 'COLLECTION_AND_DELIVERY'
      const collectedAt = completeForm.at ? new Date(completeForm.at).toISOString() : new Date().toISOString()
      await supabase.from('delivery_notes').update({
        collected_at: collectedAt,
        collected_by: user?.id || null,
        // Collection Only ends here -- the whole job is done, straight to Outbound.
        // Collection+Delivery stays PLANNED lifecycle-wise (not done yet, delivery still
        // pending) and moves onto the Deliveries board via transit_status instead.
        ...(isCollectAndDeliver
          ? { transit_status: 'IN_TRANSIT' }
          : { lifecycle_status: 'COMPLETED', completed_at: collectedAt, completed_by: user?.id || null }),
        // actual_* -- distinct from vehicle_reg/driver_name (the PLANNED values set when the
        // note was drafted for a COLLECTION_SELF/SUPPLIER note, which stay as-is) -- what was
        // actually true at the moment of confirming collected can differ from what was planned.
        actual_vehicle_reg: completeForm.vehicle || null,
        actual_driver_name: completeForm.driver || null,
        completion_notes: completeForm.notes || null,
        collection_signed: completeForm.signed,
        ...(notReady ? { forced_completion: true, forced_completion_reason: completeForceReason.trim() } : {}),
      }).eq('id', completeNote.id)

      setCompleteNote(null)
      await fetchNotes()
      showToast(`#${completeNote.serial_no} → ${isCollectAndDeliver ? 'In Transit (Deliveries board)' : 'Outbound'}`)
    } catch (err: any) {
      alert(`Error: ${err.message}`)
    } finally { setSubmitting(false) }
  }

  function openReschedule(note: DeliveryNote) {
    setRescheduleNote(note)
    setRescheduleDate(note.scheduled_date)
  }
  async function submitReschedule() {
    if (!rescheduleNote || !rescheduleDate) return
    setSubmitting(true)
    try {
      await supabase.from('delivery_notes').update({ scheduled_date: rescheduleDate }).eq('id', rescheduleNote.id)
      setRescheduleNote(null)
      await fetchNotes()
      showToast(`#${rescheduleNote.serial_no} rescheduled to ${fmtDate(rescheduleDate)}`)
    } finally { setSubmitting(false) }
  }

  async function voidNote(note: DeliveryNote) {
    const reason = window.prompt(`Void #${note.serial_no}? Optional reason:`)
    if (reason === null) return
    await supabase.from('delivery_notes').update({
      status: 'VOID', voided_at: new Date().toISOString(), voided_by: user?.id || null, void_reason: reason || null,
    }).eq('id', note.id)
    await fetchNotes()
    showToast(`#${note.serial_no} voided`)
  }

  const { scrollProps: poScrollProps, contentRef: poTableRef, bar: poScrollBar } = useStickyHScroll([visible])

  const today = ukToday()

  return (
    <WarehouseLayout warehouseId={id!}>
      <div className="space-y-4">
        {toast && (
          <div className="fixed top-4 right-4 z-50 bg-green-600 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg">✓ {toast}</div>
        )}

        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Planned Outbound</h2>
          <button onClick={fetchNotes} className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">Refresh</button>
        </div>

        {/* Date filter */}
        <div className="flex flex-wrap items-center gap-2">
          {([['today', 'Today'], ['week', 'This Week'], ['month', 'This Month'], ['next7', 'Next 7 Days']] as [DateFilter, string][]).map(([k, label]) => (
            <button key={k} onClick={() => setDateFilter(k)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg ${dateFilter === k ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50'}`}>
              {label}
            </button>
          ))}
          <button onClick={() => setDateFilter('custom')}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg ${dateFilter === 'custom' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50'}`}>
            Custom
          </button>
          {dateFilter === 'custom' && (
            <>
              <input type="date" className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 text-xs dark:bg-gray-700 dark:text-white" value={customFrom} onChange={e => setCustomFrom(e.target.value)} />
              <span className="text-xs text-gray-400">to</span>
              <input type="date" className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 text-xs dark:bg-gray-700 dark:text-white" value={customTo} onChange={e => setCustomTo(e.target.value)} />
            </>
          )}
          <span className="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-1" />
          {([['all', 'All'], ['ready', 'Ready Only'], ['not_ready', 'Not Ready Only']] as [ReadyFilter, string][]).map(([k, label]) => (
            <button key={k} onClick={() => setReadyFilter(k)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg ${readyFilter === k ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50'}`}>
              {label}
            </button>
          ))}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold tracking-wide mb-1">Collection Only</p>
            <p className="text-3xl font-bold text-sky-700 dark:text-sky-400">{plannedCollectionOnly}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold tracking-wide mb-1">Collection + Delivery</p>
            <p className="text-3xl font-bold text-orange-700 dark:text-orange-400">{plannedCollectAndDeliver}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold tracking-wide mb-1">Total PLTS / CTNS</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">{totalPlts} <span className="text-base text-gray-400">/</span> {totalCtns}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-amber-300 dark:border-amber-700 px-4 py-3">
            <p className="text-xs text-amber-600 dark:text-amber-400 uppercase font-semibold tracking-wide mb-1">⚠️ Not Ready</p>
            <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">{notReadyCount}</p>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto" {...poScrollProps}>
            {loading ? (
              <div className="py-16 text-center text-gray-400">Loading…</div>
            ) : visible.length === 0 ? (
              <div className="py-16 text-center text-gray-400">No planned outbound notes in this range.</div>
            ) : (
              <table ref={poTableRef as React.RefObject<HTMLTableElement>} className="min-w-full text-sm divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    {['READY?', 'SCHEDULED DATE', 'TYPE', 'SERIAL NO', 'JOB REF', 'SPLIT NOS', 'RELEASE TO', 'PLTS', 'CTNS', 'WT', 'STATUS', ''].map(h => (
                      <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {paged.map(note => {
                    const daysPastSchedule = daysBetween(note.scheduled_date, today)
                    const scheduleLevel = classifyOverdue(daysPastSchedule, { warnAtDays: 0, dangerAtDays: 1 })
                    const statusLbl = scheduleLevel === 'danger' ? `Overdue · ${daysPastSchedule}d` : scheduleLevel === 'warn' ? 'Today' : 'Upcoming'
                    const statusCls = scheduleLevel === 'danger' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                      : scheduleLevel === 'warn' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                      : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    return (
                      <tr key={note.id} className={scheduleLevel !== 'ok' ? OVERDUE_ROW_CLASS[scheduleLevel] : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <span title={READINESS_BADGE[readinessOf(note)].label}
                            className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${READINESS_BADGE[readinessOf(note)].cls}`}>
                            {READINESS_BADGE[readinessOf(note)].icon} {READINESS_BADGE[readinessOf(note)].label}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-xs font-semibold whitespace-nowrap text-gray-800 dark:text-gray-100">{fmtDate(note.scheduled_date)}{note.scheduled_time ? ` ${note.scheduled_time}` : ''}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_CLS[note.note_type]}`}>{TYPE_LABEL[note.note_type]}</span>
                        </td>
                        <td className="px-3 py-2.5 font-mono text-xs">#{note.serial_no}</td>
                        <td className="px-3 py-2.5 font-mono text-xs text-blue-700 dark:text-blue-400 font-semibold whitespace-nowrap">{note.job_ref}
                        <NoteSourceLink note={note} source={noteSources[note.id]} warehouseId={id!} backLabel="Planned Outbound" />
                      </td>
                        <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">{(splitNosByNote[note.id] || []).join(', ') || '—'}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 max-w-[160px] truncate" title={note.release_to_name}>
                          {note.release_to_name || '—'}
                        </td>
                        <td className="px-3 py-2.5 text-xs font-semibold font-mono text-center text-purple-700 dark:text-purple-400">{note.pallets_qty}</td>
                        <td className="px-3 py-2.5 text-xs font-semibold font-mono text-center text-blue-700 dark:text-blue-400">{note.nop}</td>
                        <td className="px-3 py-2.5 text-xs font-mono text-right text-gray-600 dark:text-gray-300">{note.weight_kg != null ? note.weight_kg.toFixed(1) : '—'}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusCls}`}>{statusLbl}</span>
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <div className="flex gap-1.5 items-center">
                            {note.pdf_url && (
                              <button onClick={() => window.open(note.pdf_url!, '_blank')}
                                className="px-2 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium">View PDF</button>
                            )}
                            <button onClick={() => openComplete(note)}
                              className="px-2 py-1 text-xs bg-green-600 text-white rounded-md hover:bg-green-700 font-medium whitespace-nowrap">
                              Confirm Collected
                            </button>
                            <button onClick={() => openReschedule(note)}
                              className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 font-medium">Reschedule</button>
                            <button onClick={() => voidNote(note)} className="px-2 py-1 text-xs text-red-500 hover:underline">Void</button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
          {!loading && visible.length > 0 && (
            <PaginationControls page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} total={pagedTotal} />
          )}
        </div>
      </div>
      <StickyHScrollbar {...poScrollBar} />

      {/* Mark Completed dialog */}
      {completeNote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
            <div className="px-6 pt-5 pb-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Confirm Collected — #{completeNote.serial_no}
              </h3>
              <button onClick={() => setCompleteNote(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
            <div className="px-6 py-4 space-y-4">
              {readinessOf(completeNote) !== 'READY' && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg px-3 py-2.5">
                  <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1.5">
                    ⚠️ {READINESS_BADGE[readinessOf(completeNote)].label} — this note's goods aren't confirmed in the warehouse yet.
                  </p>
                  <label className="block text-xs font-medium text-amber-700 dark:text-amber-400 mb-1">Reason to force-complete (required)</label>
                  <textarea rows={2} className={inputCls} value={completeForceReason} onChange={e => setCompleteForceReason(e.target.value)}
                    placeholder="e.g. goods hand-carried off-system, confirmed on site by phone…" />
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Actual Departure Time</label>
                <input type="datetime-local" className={inputCls} value={completeForm.at} onChange={e => setCompleteForm(f => ({ ...f, at: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Vehicle Reg (optional)</label>
                <input className={inputCls} value={completeForm.vehicle} onChange={e => setCompleteForm(f => ({ ...f, vehicle: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Driver Name (optional)</label>
                <input className={inputCls} value={completeForm.driver} onChange={e => setCompleteForm(f => ({ ...f, driver: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Notes (optional)</label>
                <textarea rows={2} className={inputCls} value={completeForm.notes} onChange={e => setCompleteForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                <input type="checkbox" checked={completeForm.signed} onChange={e => setCompleteForm(f => ({ ...f, signed: e.target.checked }))} />
                Signature obtained
              </label>
              {completeNote.fulfilment_type === 'COLLECTION_AND_DELIVERY' && (
                <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
                  This is a Collection + Delivery note -- confirming collected here will move it to <strong>In Transit</strong> on the Deliveries board.
                </p>
              )}
            </div>
            <div className="px-6 pb-5 flex gap-3 justify-end border-t border-gray-200 dark:border-gray-700 pt-3">
              <button onClick={() => setCompleteNote(null)} className={btnSecondary}>Cancel</button>
              <button onClick={submitComplete} disabled={submitting}
                className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                {submitting ? 'Saving…' : completeNote.fulfilment_type === 'COLLECTION_AND_DELIVERY' ? 'Confirm → In Transit' : 'Confirm → Outbound'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule dialog */}
      {rescheduleNote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm">
            <div className="px-6 pt-5 pb-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-white">Reschedule — #{rescheduleNote.serial_no}</h3>
              <button onClick={() => setRescheduleNote(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
            <div className="px-6 py-4">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">New Scheduled Date</label>
              <input type="date" className={inputCls} value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)} />
            </div>
            <div className="px-6 pb-5 flex gap-3 justify-end border-t border-gray-200 dark:border-gray-700 pt-3">
              <button onClick={() => setRescheduleNote(null)} className={btnSecondary}>Cancel</button>
              <button onClick={submitReschedule} disabled={submitting || !rescheduleDate}
                className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {submitting ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </WarehouseLayout>
  )
}
